// @ts-nocheck

import React, {
  createContext, useState, useEffect, Dispatch, SetStateAction, useLayoutEffect
} from 'react';
import { toast } from 'react-toastify';

import api from 'services/api';

import { IAuthentication, User } from 'types/authentication';
import { createNoSubstitutionTemplateLiteral } from 'typescript';

interface AuthContextItems {
  accessToken: string;
  user: User;
  setUser: Dispatch<SetStateAction<User>>;
  loading: boolean;
  signIn(credentials: object): Promise<void>;
  signOut(): Promise<void>;
  updateUser(): Promise<void>;
  rememberUser: boolean;
  setRememberUser: Function;
  checkIfFirstTimeUsingApplication: Function;
  refreshToken: Function;
}

export const AuthContext = createContext<AuthContextItems>(
  {} as AuthContextItems,
);

export const AuthProvider: React.FC = ({ children }) => {
  const [user, setUser] = useState<User>(() => {
    const user = localStorage.getItem('@RepfyProvider: user');

    if (user) {
      return JSON.parse(user) as User;
    }

    return {} as User;
  });
  const [accessToken, setAccessToken] = useState<string>(() => {
    const token = localStorage.getItem('@RepfyProvider: accessToken');

    if (token) {
      return token;
    }

    return '';
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [rememberUser, setRememberUser] = useState<boolean>(false);

  useLayoutEffect(() => {
    setTokenOnApiHeaders(accessToken)
  }, []);

  useEffect(() => {
    loadLocalStorageData();
    checkIfFirstTimeUsingApplication();
  }, []);

  useEffect(() => {
    localStorage.setItem('@RepfyProvider: user', JSON.stringify(user));
  }, [user]);

  const checkDocuments = async (user: User) => {
    try {
      const response = await api.get<{
        results: {
          mandatory: boolean;
          id: number;
        }[];
      }>('/document-types');

      const mandatoryDocumentIds = response?.data?.results.filter((documentType) => documentType.mandatory).map((documentType) => documentType.id) || [];

      const userDocuments = user?.documents.filter((document) => mandatoryDocumentIds.includes(Number(document?.document_type_id))) || [];

    
      const userDocumentsByType = userDocuments.reduce((acc,item)=>{
          if(!acc[item.document_type_id]){
            acc[item.document_type_id] = item;
          }else{
            if(acc[item.document_type_id].status_id !== 2 && item.status_id === 2){
              acc[item.document_type_id] = item;
            }
          }
          return acc;
      },{});
      
      const statusByDocumentTypeId = mandatoryDocumentIds.map((id) => {
        const foundDocument = Object.values(userDocumentsByType).find((document) => Number(document.document_type_id) === id);

        if (!foundDocument) {
          return 0;
        }

        return foundDocument.status_id;
      });


      const isMissing = statusByDocumentTypeId.some((status) => status === 0);

      if (isMissing) {
        return 0;
      }

      const isApproved = statusByDocumentTypeId.every((status) => status === 2);

      if (isApproved) {
        return 2;
      }

      const isRejected = statusByDocumentTypeId.some((status) => status === 3);

      if (isRejected) {
        return 3;
      }

      return 1;

    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || 'Algo deu errado');
    }
  }

  const checkIfFirstTimeUsingApplication = () => {
    const checkedGuest = localStorage.getItem('@RepfyProvider: isGuest');

    const isGuest = checkedGuest === 'not_guest';

    return isGuest;
  };

  const loadLocalStorageData = () => {
    setLoading(true);

    const storageUser = localStorage.getItem('@RepfyProvider: user');
    const storageToken = localStorage.getItem('@RepfyProvider: accessToken');
    const storageRefreshToken = localStorage.getItem(
      '@RepfyProvider: refreshToken',
    );

    if (storageToken && storageUser && storageRefreshToken) {
      setAccessToken(storageToken);

      setRememberUser(true);

      setUser(prevState => ({
        ...prevState,
        ...JSON.parse(storageUser)
      }));
    }

    if(storageRefreshToken){
      refreshToken(storageRefreshToken);
    }

    setLoading(false);
  };

  const refreshToken = async (refreshToken: string) => {
    const token = {
      refresh_token: refreshToken,
    };

    try {
      const response = await api.put<IAuthentication>(
        `sessions/refresh-token`,
        token,
      );

      /* const documents_status = response?.data?.user?.documents
        ? response?.data?.user?.documents[0]?.status_id || 0
        : 0 */

      const documents_status = await checkDocuments(response?.data?.user) || 0;

      const updatedUser = {
        ...user,
        ...response?.data?.user,
        documents_status
      }

      setUser(updatedUser)

      setLocalStorage(
        updatedUser,
        response?.data?.access_token,
        response?.data?.refresh_token,
      );
    } catch (error: any) {
      handleError(error?.response?.data?.message || error?.toString());

      signOut();
    }
  };

  const signIn = async (credentials: object) => {
    const response = await api.post<IAuthentication>('sessions', credentials, {
      params: {
        role_id: '3',
      }
    });

    // const documents_status = response?.data?.user?.documents[0]?.status_id || 0

    const documents_status = await checkDocuments(response?.data?.user) || 0;

    const userData = {
      ...response?.data?.user,
      documents_status
    }

    setUser(userData);

    setAccessToken(response?.data?.access_token);

    setTokenOnApiHeaders(response?.data?.access_token);

    localStorage.setItem('@RepfyProvider: isGuest', 'not_guest');

    rememberUser &&
      setLocalStorage(
        userData,
        response?.data?.access_token,
        response?.data?.refresh_token,
      );
  };

  const signOut = async () => {
    try {
      await api.delete(`sessions/logout`);

      localStorage.removeItem('@RepfyProvider: user');
      localStorage.removeItem('@RepfyProvider: accessToken');
      localStorage.removeItem('@RepfyProvider: refreshToken');
      localStorage.removeItem('@RepfyProvider: isGuest');
      localStorage.removeItem('@RepfyProvider: DocumentsApproved')

      setUser({} as User);

      setAccessToken('');
    } catch (error: any) {
      handleError(error?.response?.data?.message || error?.toString());
    }
  };

  const handleError = (error: string) => {
    toast.error(error);
  };

  const updateUser = async () => {
    try {
      const response = await api.get<User>(`/users/${user?.id}`);

      const documents_status = response?.data?.documents
        ? response?.data?.documents[0]?.status_id || 0
        : 0

      const userUpdated = {
        ...response?.data,
        documents_status
      }

      setUser(userUpdated);
    } catch (error: any) {
      handleError(error?.response?.data?.message || error?.toString());
    }
  }

  const setLocalStorage = (
    user: object,
    token: string,
    refreshToken: string,
  ) => {
    localStorage.setItem('@RepfyProvider: user', JSON.stringify(user));
    localStorage.setItem('@RepfyProvider: accessToken', token);
    localStorage.setItem('@RepfyProvider: refreshToken', refreshToken);
  };

  const setTokenOnApiHeaders = (token: string) => {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  };

  return (
    <AuthContext.Provider
      value={{
        accessToken,
        user,
        setUser,
        loading,
        signIn,
        signOut,
        updateUser,
        rememberUser,
        setRememberUser,
        checkIfFirstTimeUsingApplication,
        refreshToken
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
